import { FunctionComponent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as Clock } from "@assets/icons/inbox/clock.svg";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { ScopeListRow } from "./utils";

interface ListScopeFilterRowProps {
	row: ScopeListRow;
	isSelected: boolean;
	/** Dim the row and tag it when shown inside the collapsed Archived section. */
	isArchivedSection?: boolean;
	/** Dim the row when shown inside the "nothing on this surface yet" section. */
	isIrrelevantSection?: boolean;
	/** Jump to the surface this list's leads actually live on, pre-scoped to the list. */
	crossLink?: { href: string; label: string } | null;
	onToggle: (id: number) => void;
}

const STATUS_DOT_CLASS: Record<ScopeListRow["status"], string> = {
	active: "bg-status-success",
	paused: "bg-status-warning",
	none: "",
};

export const ListScopeFilterRow: FunctionComponent<ListScopeFilterRowProps> = ({
	row,
	isSelected,
	isArchivedSection = false,
	isIrrelevantSection = false,
	crossLink,
	onToggle,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list" });

	const isMuted = isArchivedSection || isIrrelevantSection;
	const showDot = !isMuted && row.status !== "none";

	// The link sits inside the row's own click target, which toggles selection — without this,
	// following it would also (de)select the list on the way out.
	const handleCrossLinkClick = (event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation();

	return (
		<div
			className={classNames(
				"flex items-center gap-2 h-11 px-3 cursor-pointer duration-200",
				isSelected ? "bg-selected-item" : "hover:bg-gray-100"
			)}
			data-test="list-scope-filter-row"
			onClick={() => onToggle(row.id)}
		>
			<div className="w-5 flex-none">
				<Checkbox isChecked={isSelected} />
			</div>

			<div className="w-3.5 flex-none flex items-center justify-center text-text-meta">
				{!isMuted && row.isRunning && <Clock className="w-3.5 h-3.5" />}
			</div>

			<span
				className={classNames(
					"grow text-sm truncate",
					isMuted ? "text-text-meta font-normal" : "text-gray-900 font-medium"
				)}
			>
				{row.title}
				{isArchivedSection && (
					<span className="ml-1.5 align-middle text-[9px] font-semibold uppercase tracking-wide text-text-meta">
						{t("scopeFilter.archivedTag")}
					</span>
				)}
			</span>

			{row.total !== null && (
				<span
					className={classNames(
						"flex-shrink-0 text-xs tabular-nums",
						isMuted ? "text-text-meta" : "text-gray-500"
					)}
				>
					{row.assigned}/{row.total}
				</span>
			)}

			{crossLink && (
				<a
					aria-label={crossLink.label}
					className="flex-none flex items-center gap-1 px-1.5 py-1 rounded-md text-[11px] font-medium text-text-meta cursor-pointer hover:bg-gray-200 hover:text-brand-light duration-200"
					data-test="list-scope-cross-link"
					href={crossLink.href}
					title={crossLink.label}
					onClick={handleCrossLinkClick}
				>
					<Icon className="w-3 h-3" icon={faArrowUpRightFromSquare} />
				</a>
			)}

			<span className="w-2 flex-none flex items-center justify-center">
				{showDot && (
					<span
						className={classNames("w-[7px] h-[7px] rounded-full", STATUS_DOT_CLASS[row.status])}
					/>
				)}
			</span>
		</div>
	);
};
